import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react'

interface PullToRefreshProps {
  onRefresh: () => Promise<void>
  children: ReactNode
}

// Find the nearest scrollable ancestor
function getScrollParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null

  let parent = element.parentElement
  while (parent) {
    const { overflow, overflowY } = getComputedStyle(parent)
    if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
      return parent
    }
    parent = parent.parentElement
  }
  return document.documentElement
}

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollParentRef = useRef<HTMLElement | null>(null)
  const startY = useRef(0)
  const isPulling = useRef(false)

  const THRESHOLD = 60
  const MAX_PULL = 100

  useEffect(() => {
    scrollParentRef.current = getScrollParent(containerRef.current)
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isRefreshing) return

    const scrollParent = scrollParentRef.current
    const isAtTop = !scrollParent || scrollParent.scrollTop <= 0

    if (isAtTop) {
      startY.current = e.touches[0].clientY
      isPulling.current = true
    }
  }, [isRefreshing])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) return

    const currentY = e.touches[0].clientY
    const diff = currentY - startY.current

    if (diff > 0) {
      // Apply resistance to the pull
      const resistance = 0.4
      const pull = Math.min(diff * resistance, MAX_PULL)
      setPullDistance(pull)
    }
  }, [isRefreshing])

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return
    isPulling.current = false

    if (pullDistance >= THRESHOLD && !isRefreshing) {
      setIsRefreshing(true)
      setPullDistance(THRESHOLD * 0.6) // Keep spinner visible

      try {
        await onRefresh()
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
  }, [pullDistance, isRefreshing, onRefresh])

  return (
    <div
      ref={containerRef}
      className="min-h-full"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-all duration-200"
        style={{ height: pullDistance }}
      >
        {pullDistance > 0 && (
          <div
            className={`${isRefreshing ? 'animate-spin' : ''}`}
            style={{
              opacity: Math.min(pullDistance / THRESHOLD, 1),
              transform: `rotate(${(pullDistance / THRESHOLD) * 360}deg)`,
            }}
          >
            <svg
              className="h-6 w-6 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
        )}
      </div>

      {children}
    </div>
  )
}
